import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, X, AlertCircle, Volume2, Settings, Key, Disc } from "lucide-react";

const getVirtualSlideHTML = (slide) => {
    if (!slide) return "No active slide content.";
    let html = `<div class="slide" id="${slide.id || ""}" mode="${slide.mode || ""}">`;
    if (slide.title) html += `<h1>${slide.title}</h1>`;
    if (slide.kicker) html += `<p class="kicker">${slide.kicker}</p>`;
    if (slide.subtitle) html += `<h2>${slide.subtitle}</h2>`;
    
    if (slide.fragments && slide.fragments.length > 0) {
        html += `<ul class="fragments">`;
        slide.fragments.forEach((f, idx) => {
            html += `<li class="fragment" index="${idx}">${f.text || f.caption || ""}</li>`;
        });
        html += `</ul>`;
    }
    
    if (slide.bullets && slide.bullets.length > 0) {
        html += `<ul class="bullets">`;
        slide.bullets.forEach(b => {
            html += `<li>${b}</li>`;
        });
        html += `</ul>`;
    }
    
    if (slide.explanation) html += `<p class="explanation">${slide.explanation}</p>`;
    
    // MCQ specific details
    if (slide.mode === "mcq_display" && slide.mcqs) {
        html += `<div class="mcq">`;
        slide.mcqs.forEach(m => {
            html += `<p class="question">${m.question}</p>`;
            if (m.options) {
                html += `<ul class="options">`;
                m.options.forEach(o => {
                    html += `<li>${o.text} ${o.isCorrect ? "(Correct)" : ""}</li>`;
                });
                html += `</ul>`;
            }
        });
        html += `</div>`;
    }
    
    html += `</div>`;
    return html;
};

export const VoiceAssistantModal = ({ isOpen, onClose, sessionChannel, activeLesson, slideIndex, state, sendCommand }) => {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY || process.env.GEMINI_API_KEY || "";
    const [isRecording, setIsRecording] = useState(false);
    const [status, setStatus] = useState("Idle");
    const [error, setError] = useState(null);

    const wsRef = useRef(null);
    const audioCtxRef = useRef(null);
    const streamRef = useRef(null);
    const processorRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            stopAll();
        }
    }, [isOpen]);

    const stopAll = () => {
        setIsRecording(false);
        setStatus("Idle");
        if (wsRef.current) {
            try {
                wsRef.current.close();
            } catch (e) {}
            wsRef.current = null;
        }
        if (processorRef.current) {
            try {
                processorRef.current.disconnect();
            } catch (e) {}
            processorRef.current = null;
        }
        if (audioCtxRef.current) {
            try {
                audioCtxRef.current.close();
            } catch (e) {}
            audioCtxRef.current = null;
        }
        if (streamRef.current) {
            try {
                streamRef.current.getTracks().forEach(t => t.stop());
            } catch (e) {}
            streamRef.current = null;
        }
        // Tell smartboard to halt active audio streams
        if (sessionChannel?.send) {
            sessionChannel.send({ type: "AI_AUDIO_STOP" });
        }
        if (sendCommand) {
            sendCommand("RESUME_TEACHING", { aiPaused: false, mascotState: "idle" });
        }
    };

    const startRecording = async () => {
        if (!apiKey) {
            setError("Gemini API key is not configured in the environment variables.");
            return;
        }
        setError(null);
        setStatus("Initializing...");

        try {
            // 1. Mic permission and MediaStream setup
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            // 2. Audio Context for downsampling to 16000Hz PCM
            const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
            audioCtxRef.current = audioContext;

            const source = audioContext.createMediaStreamSource(stream);
            // ScriptProcessorNode size 2048, 1 input channel, 1 output channel
            const processor = audioContext.createScriptProcessor(2048, 1, 1);
            processorRef.current = processor;

            // 3. Gemini Multimodal Live API WebSocket on v1beta
            const wsUrl = `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContent?key=${encodeURIComponent(apiKey)}`;
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                setStatus("Connecting to Gemini...");
                
                // Construct virtual slide representation
                const slide = activeLesson?.slides?.[slideIndex] || null;
                const slideHTML = getVirtualSlideHTML(slide);
                const topicName = activeLesson?.sessionName || "Classroom Lesson";

                // Setup the session instructions using gemini-2.0-flash-exp
                const setupMsg = {
                    setup: {
                        model: "models/gemini-2.0-flash-exp",
                        generationConfig: {
                            responseModalities: ["AUDIO"],
                            speechConfig: {
                                voiceConfig: {
                                    prebuiltVoiceConfig: {
                                        voiceName: "Puck"
                                    }
                                }
                            }
                        },
                        systemInstruction: {
                            parts: [
                                {
                                    text: `You are Sparkl, a highly engaging, friendly, and concise AI co-teacher inside a classroom.
The teacher is using a mobile/laptop remote control to present to the class.
Current Classroom Topic: "${topicName}"
Active Slide Content (rendered on Smartboard):
${slideHTML}

Your task:
The teacher/student is speaking a question in audio format.
Respond in a friendly, conversational, and highly engaging manner suitable for high school students.
Be extremely concise: maximum 2 to 3 sentences so you do not disrupt the flow of the classroom.
Respond strictly in audio format. The response will play on the main classroom smartboard.`
                                }
                            ]
                        }
                    }
                };

                ws.send(JSON.stringify(setupMsg));
                
                // Broadcast to smartboard that a voice session is starting
                if (sessionChannel?.send) {
                    sessionChannel.send({ type: "AI_AUDIO_START" });
                }
                if (sendCommand) {
                    sendCommand("PAUSE_TEACHING", { aiPaused: true, mascotState: "listening" });
                }

                setIsRecording(true);
                setStatus("Listening... Speak now!");
            };

            ws.onerror = (e) => {
                console.error("Gemini WebSocket error:", e);
                setError("Connection error. Check your API Key or network.");
                stopAll();
            };

            ws.onclose = (e) => {
                setIsRecording(false);
                setStatus("Closed");
                if (sendCommand) {
                    sendCommand("RESUME_TEACHING", { aiPaused: false, mascotState: "idle" });
                }
            };

            ws.onmessage = async (event) => {
                try {
                    const data = JSON.parse(event.data);
                    
                    // Look for inline server content (audio chunks)
                    const parts = data?.serverContent?.modelTurn?.parts;
                    if (parts) {
                        for (const part of parts) {
                            if (part.inlineData && part.inlineData.data) {
                                setStatus("Streaming response to smartboard...");
                                if (sendCommand) {
                                    sendCommand("PAUSE_TEACHING", { aiPaused: true, mascotState: "speaking" });
                                }
                                if (sessionChannel?.send) {
                                    sessionChannel.send({
                                        type: "AI_AUDIO_CHUNK",
                                        payload: part.inlineData.data
                                    });
                                }
                            }
                        }
                    }

                    if (data?.serverContent?.turnComplete) {
                        setStatus("Response completed!");
                        // Notify smartboard that the turn is complete
                        if (sessionChannel?.send) {
                            sessionChannel.send({ type: "AI_AUDIO_END" });
                        }
                    }
                } catch (e) {
                    console.error("Error processing WebSocket message:", e);
                }
            };

            // Connect audio processor to stream PCM chunks
            source.connect(processor);
            processor.connect(audioContext.destination);

            processor.onaudioprocess = (e) => {
                if (ws.readyState !== WebSocket.OPEN) return;
                const channelData = e.inputBuffer.getChannelData(0);

                // Convert Float32Array to 16-bit Signed PCM Int16Array
                const pcm16 = new Int16Array(channelData.length);
                for (let i = 0; i < channelData.length; i++) {
                    pcm16[i] = Math.max(-32768, Math.min(32767, channelData[i] * 32768));
                }

                // Base64 encode
                const binary = String.fromCharCode(...new Uint8Array(pcm16.buffer));
                const base64 = btoa(binary);

                const audioChunk = {
                    realtimeInput: {
                        mediaChunks: [
                            {
                                mimeType: "audio/pcm",
                                data: base64
                            }
                        ]
                    }
                };

                ws.send(JSON.stringify(audioChunk));
            };

        } catch (e) {
            console.error("Failed to initialize recording:", e);
            setError("Could not access microphone. Please check permissions.");
            stopAll();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-[#0b1218]/70 backdrop-blur-md"
                >
                    {/* Modal container */}
                    <motion.div
                        initial={{ scale: 0.94, y: 15, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.94, y: 12, opacity: 0 }}
                        transition={{ type: "spring", damping: 25, stiffness: 350 }}
                        className="relative w-full max-w-md rounded-3xl p-6 bg-white/90 backdrop-blur-xl border border-black/[0.08] shadow-[0_24px_70px_rgba(20,32,42,0.25)] overflow-hidden"
                    >
                        {/* Background glow orbs */}
                        <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-[#5BC2CC]/15 blur-3xl pointer-events-none" />
                        <div className="absolute -bottom-24 -left-24 w-48 h-48 rounded-full bg-[#7C6BF2]/10 blur-3xl pointer-events-none" />

                        {/* Top controls */}
                        <div className="flex items-center justify-between mb-4 relative z-10">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#ECE7FE] text-[#4A38B8] text-[10px] uppercase tracking-[0.2em] font-semibold">
                                <Volume2 className="w-3 h-3" />
                                Smartboard Voice Assistant
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={onClose}
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#4a5662] hover:bg-black/[0.05] transition"
                                >
                                    <X className="w-4.5 h-4.5" />
                                </button>
                            </div>
                        </div>

                        {/* Error box */}
                        {error && (
                            <div className="mb-4 p-3.5 rounded-2xl bg-red-50 border border-red-100 flex items-start gap-2 text-red-600 text-xs relative z-10">
                                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                <p className="leading-snug">{error}</p>
                            </div>
                        )}

                        {/* Core interface */}
                        <div className="flex flex-col items-center py-6 relative z-10">
                            {/* Animated waveform container */}
                            <div className="w-56 h-56 flex items-center justify-center relative mb-6">
                                {/* Glowing backdrop circles */}
                                <AnimatePresence>
                                    {isRecording && [0, 0.6, 1.2].map((delay, i) => (
                                        <motion.div
                                            key={i}
                                            className="absolute inset-4 rounded-full border border-cyan-400/40 pointer-events-none"
                                            initial={{ scale: 0.9, opacity: 0.7 }}
                                            animate={{ scale: 1.8, opacity: 0 }}
                                            transition={{
                                                duration: 2.2,
                                                repeat: Infinity,
                                                delay,
                                                ease: "easeOut"
                                            }}
                                        />
                                    ))}
                                </AnimatePresence>

                                {/* Glowing orb itself */}
                                <motion.button
                                    onClick={isRecording ? stopAll : startRecording}
                                    className="w-28 h-28 rounded-full flex items-center justify-center relative shadow-lg focus:outline-none"
                                    style={{
                                        background: "linear-gradient(135deg, #7C6BF2, #5BC2CC, #FFA987)",
                                        boxShadow: isRecording
                                            ? "0 0 36px rgba(91,194,204,0.7), inset 0 2px 8px rgba(255,255,255,0.4)"
                                            : "0 12px 28px rgba(91,194,204,0.3)"
                                    }}
                                    animate={{
                                        scale: isRecording ? [1, 1.06, 1] : 1
                                    }}
                                    transition={{
                                        duration: 1.6,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                >
                                    <motion.div
                                        animate={{ rotate: isRecording ? 360 : 0 }}
                                        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                                        className="absolute -inset-1.5 rounded-full border-2 border-dashed border-white/30"
                                    />
                                    {isRecording ? (
                                        <Disc className="w-10 h-10 text-white animate-pulse" />
                                    ) : (
                                        <Mic className="w-10 h-10 text-white" />
                                    )}
                                </motion.button>
                            </div>

                            <h3 className="text-xl font-semibold text-[#14202a] text-center tracking-tight mb-1.5">
                                {isRecording ? "Listening to your voice" : "Ask classroom AI assistant"}
                            </h3>
                            <p className="text-xs text-[#4a5662] text-center max-w-[280px] leading-relaxed mb-6">
                                {isRecording ? "Speak clearly. Your question and current slide context are being analyzed." : "Click the microphone to start streaming your question directly to the smartboard."}
                            </p>

                            {/* Status pill */}
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/[0.04] border border-black/[0.05]">
                                <span className={`w-1.5 h-1.5 rounded-full ${isRecording ? "bg-red-500 animate-pulse" : "bg-[#4a5662]"}`} />
                                <span className="text-[10px] uppercase tracking-[0.15em] text-[#4a5662] font-semibold">
                                    {status}
                                </span>
                            </div>
                        </div>

                        {/* Helper tip */}
                        <div className="pt-4 mt-2 border-t border-black/[0.05] text-center relative z-10">
                            <p className="text-[10px] text-[#4a5662] leading-snug">
                                Sparkl will reply instantly out of the **Student Display** speakers for all students to hear.
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default VoiceAssistantModal;
